/**
 * Copy text to the clipboard, falling back for insecure contexts.
 *
 * `navigator.clipboard` exists only in a secure context (HTTPS or localhost),
 * so the deprecated `execCommand` path is what makes copying work at all on a
 * non-TLS deployment.
 *
 * Extracted because the same pair was hand-rolled in five components; those
 * call sites are left alone for now.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  if (!text) {
    return false;
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied, or an unfocused document. Fall through rather than
      // reporting failure while a working fallback exists.
    }
  }

  return copyViaTextarea(text);
}

function copyViaTextarea(text: string): boolean {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Offscreen rather than hidden: a `display: none` element cannot hold a
  // selection, so the copy would find nothing.
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-1000px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);

  try {
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}
